import { Advertiser, CommercialType } from '@talon/web/lib/api';
import { IAddress } from '@talon/web/src/utils/decodeGoogle';
import { Box, Text } from 'grommet';
import { Checkmark } from 'grommet-icons';
import { IMediaAssetLengthMap, IRolePerformer, formatDefaultName } from '..';

interface ISummaryTabProps {
  name: string;
  client: Partial<Advertiser> | undefined;
  product: string;
  budget: number;
  union: string | undefined;
  code: string;
  geography: string | undefined;
  place: IAddress | undefined;
  exclusivity: string | undefined;
  media: string[] | undefined;
  assetCounts: IMediaAssetLengthMap;
  rolePerformers: IRolePerformer[];
}

export const SummaryTab = (props: ISummaryTabProps) => {
  const {
    name,
    client,
    code,
    product,
    budget,
    union,
    geography,
    place,
    exclusivity,
    media,
    assetCounts,
    rolePerformers,
  } = props;
  const checkmark = (label: string) => (
    <Text size="small">
      (<Checkmark color="status-ok" size="small" />
      {label})
    </Text>
  );
  return (
    <Box pad="medium" gap="medium">
      <Text>
        Below is a summary of everything you've filled in. Please confirm this is how you want to set the project up.
      </Text>
      {/* print all of the state variables */}
      <Box gap="small">
        {name && <Text>Name: {name}</Text>}
        {code && <Text>Job Code: {code}</Text>}
        {client && (
          <Text>
            Client: {client.name} {client.isJpcAuthorizer && checkmark('JPC Authorizer')}{' '}
            {client.isSagSignatory && checkmark('SAG Signatory')}
          </Text>
        )}
        {product && <Text>Product: {product}</Text>}
        {!!budget && (
          <Text>Budget: {new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(budget)}</Text>
        )}
        {union && <Text>This {union === 'union' ? 'is a' : 'is not a'} union job.</Text>}
        {geography && (
          <Text>
            It will be shot {geography === 'usa' ? 'onshore' : 'offshore'}
            {place && ` at ${place.street_address} ${place.city}, ${place.state} ${place.zip}`}.
          </Text>
        )}
        {exclusivity && <Text>The client {exclusivity === 'yes' ? 'does' : 'does not'} require exclusivity.</Text>}
        {!!media?.length && <Text>The following media and assets will be produced:</Text>}
        {media?.map((medium) => (
          <Box direction="row" gap="small" pad={{ left: 'large' }} key={medium}>
            <Text>{medium}</Text>
            {assetCounts[medium as keyof typeof CommercialType]?.map((length) => (
              <Text key={`${medium}-${length}`}>{length} sec.</Text>
            ))}
          </Box>
        ))}
        {rolePerformers?.length && <Text>The following roles will be cast:</Text>}
        <Box pad={{ left: 'large' }} direction="column">
          <Box direction="row" gap="small" background="dark-1" pad="small">
            <Text>Name</Text>
            <Text>Type</Text>
            <Text>Performer Type</Text>
          </Box>
          {rolePerformers.map((rolePerformer, i) => (
            <Box
              direction="row"
              gap="small"
              key={`${rolePerformer.name}-${i}`}
              background={i % 2 === 0 ? 'light-1' : 'light-2'}
              border="all"
              pad="small"
            >
              <Text>{formatDefaultName(rolePerformer)}</Text>
              <Text>{rolePerformer.roleType}</Text>
              <Text>{rolePerformer.performerType}</Text>
            </Box>
          ))}
        </Box>
      </Box>
    </Box>
  );
};
